import React, { useEffect, useState } from 'react'
import {
	ArrowBack,
	Delete,
	ExpandLess,
	ExpandMore,
} from '@mui/icons-material'
import {
	Box,
	Button,
	Container,
	Divider,
	IconButton,
	Tab,
	Tabs,
	TextField,
	Typography,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { doDelete, doGet, doPost, doPut } from '../../util/do-fetch'

function TemplateSection({ type }) {
	const [templates, setTemplates]   = useState([])
	const [expanded, setExpanded]     = useState(null) // id of expanded row
	const [editName, setEditName]     = useState('')
	const [editBody, setEditBody]     = useState('')
	const [saving, setSaving]         = useState(false)
	const [addingNew, setAddingNew]   = useState(false)
	const [newName, setNewName]       = useState('')
	const [newBody, setNewBody]       = useState('')

	useEffect(() => {
		loadTemplates()
	}, [type])

	const loadTemplates = () => {
		doGet(`/admin/prompt-templates?type=${type}`)
			.then(r => r.json())
			.then(data => setTemplates(data || []))
			.catch(console.error)
	}

	const handleExpand = (t) => {
		if (expanded === t.id) {
			setExpanded(null)
		} else {
			setExpanded(t.id)
			setEditName(t.name)
			setEditBody(t.body)
		}
	}

	const handleSave = async (id) => {
		setSaving(true)
		try {
			await doPut(`/admin/prompt-templates/${id}`, {
				body: JSON.stringify({ name: editName, body: editBody }),
			})
			setExpanded(null)
			loadTemplates()
		} catch (err) {
			console.error(err)
		} finally {
			setSaving(false)
		}
	}

	const handleDelete = async (id) => {
		if (!window.confirm('Delete this template?')) return
		try {
			await doDelete(`/admin/prompt-templates/${id}`)
			if (expanded === id) setExpanded(null)
			loadTemplates()
		} catch (err) {
			console.error(err)
		}
	}

	const handleAddNew = async () => {
		if (!newName.trim() || !newBody.trim()) {
			alert('Name and body are required.')
			return
		}
		setSaving(true)
		try {
			await doPost('/admin/prompt-templates', {
				body: JSON.stringify({ type, name: newName.trim(), body: newBody.trim() }),
			})
			setAddingNew(false)
			setNewName('')
			setNewBody('')
			loadTemplates()
		} catch (err) {
			console.error(err)
		} finally {
			setSaving(false)
		}
	}

	return (
		<Box>
			{templates.map(t => (
				<Box key={t.id} sx={{ mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
					<Box
						display="flex"
						alignItems="center"
						justifyContent="space-between"
						sx={{ px: 2, py: 1, cursor: 'pointer' }}
						onClick={() => handleExpand(t)}
					>
						<Box>
							<Typography variant="body1" fontWeight={500}>{t.name}</Typography>
							{expanded !== t.id && (
								<Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: 11 }}>
									{t.body.slice(0, 120)}{t.body.length > 120 ? '…' : ''}
								</Typography>
							)}
						</Box>
						<Box display="flex" alignItems="center">
							<IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(t.id) }}>
								<Delete fontSize="small" />
							</IconButton>
							<IconButton size="small">
								{expanded === t.id ? <ExpandLess /> : <ExpandMore />}
							</IconButton>
						</Box>
					</Box>

					{expanded === t.id && (
						<Box sx={{ px: 2, pb: 2 }}>
							<Divider sx={{ mb: 1.5 }} />
							<TextField
								label="Name"
								fullWidth
								size="small"
								value={editName}
								onChange={e => setEditName(e.target.value)}
								sx={{ mb: 1.5 }}
							/>
							<TextField
								label="Body"
								fullWidth
								multiline
								rows={6}
								value={editBody}
								onChange={e => setEditBody(e.target.value)}
								InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
							/>
							<Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
								<Button size="small" onClick={() => setExpanded(null)} sx={{ mr: 1 }}>
									Cancel
								</Button>
								<Button size="small" variant="contained" disabled={saving} onClick={() => handleSave(t.id)}>
									Save
								</Button>
							</Box>
						</Box>
					)}
				</Box>
			))}

			{addingNew ? (
				<Box sx={{ mt: 1, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
					<Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>New Template</Typography>
					<TextField
						label="Name"
						fullWidth
						size="small"
						value={newName}
						onChange={e => setNewName(e.target.value)}
						sx={{ mb: 1.5 }}
					/>
					<TextField
						label="Body"
						fullWidth
						multiline
						rows={6}
						value={newBody}
						onChange={e => setNewBody(e.target.value)}
						InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
					/>
					<Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
						<Button size="small" onClick={() => { setAddingNew(false); setNewName(''); setNewBody('') }} sx={{ mr: 1 }}>
							Cancel
						</Button>
						<Button size="small" variant="contained" disabled={saving} onClick={handleAddNew}>
							Add
						</Button>
					</Box>
				</Box>
			) : (
				<Button
					size="small"
					variant="outlined"
					sx={{ mt: 1 }}
					onClick={() => setAddingNew(true)}
				>
					+ Add New
				</Button>
			)}
		</Box>
	)
}

function PromptTemplates() {
	const navigate  = useNavigate()
	const [tab, setTab] = useState(0)

	return (
		<Container maxWidth="md">
			<Box mt={4}>
				<Box display="flex" flexDirection="row" alignItems="center">
					<IconButton onClick={() => navigate('/admin')}>
						<ArrowBack />
					</IconButton>
					<Typography variant="h5">Topic/Question Prompts</Typography>
				</Box>
				<Divider sx={{ my: 2 }} />

				<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
					<Tab label="Topic Prompts" />
					<Tab label="Question Prompts" />
				</Tabs>

				{tab === 0 && <TemplateSection type="topic" />}
				{tab === 1 && <TemplateSection type="question" />}
			</Box>
		</Container>
	)
}

export default PromptTemplates
